import { useCallback, useEffect, useState } from 'react'
import { Search, ShieldAlert, X } from 'lucide-react'
import { searchPIIAccess, fetchPIICategories, type PIIAccessRow } from './piiAccessApi'

/**
 * A18.03 — PII kirish jurnali. Shaxsiy/kredensial ma'lumotga KIM kirgani
 * (DB-brauzer orqali) alohida qayd etiladi (`admin_audit` `pii.*` amallari).
 * FAQAT superadmin (endpoint route-gated) — read-only.
 *
 * i18n HOT fayllarga tegmaslik uchun matnlar shu yerда lokal (boshqa yangi
 * subsystem ekranlari bilan bir xil naqsh).
 */

// Lokal matnlar (RU/UZ aralashmaydi — panel o'zbekcha).
const S = {
  category: 'Toifa',
  anyCategory: 'Har qanday toifa',
  staff: 'Xodim',
  subject: 'Obyekt',
  since: 'Dan',
  until: 'Gacha',
  search: 'Qidirish',
  clear: 'Tozalash',
  hint: "Shaxsiy ma'lumotga kirishlar shu yerda ko'rinadi. Filtrni tanlab qidiring.",
  empty: 'Hech narsa topilmadi',
  results: (n: number) => `${n} ta yozuv`,
  time: 'Vaqt',
  target: 'Obyekt',
  detail: 'Tafsilot',
  ip: 'IP',
  catLabel: (c: string) =>
    c === 'credentials' ? 'Kredensial' : c === 'personal_data' ? 'Shaxsiy' : c,
}

function dayStart(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(`${v}T00:00:00`).getTime()
  return Number.isFinite(t) ? t : undefined
}
function dayEnd(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(`${v}T23:59:59`).getTime()
  return Number.isFinite(t) ? t : undefined
}
function fmtTime(ts: number): string {
  if (!Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

function CatBadge({ c }: { c: string }) {
  const tone =
    c === 'credentials'
      ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
      : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>
      {S.catLabel(c)}
    </span>
  )
}

export function PIIAccessLog() {
  const [categories, setCategories] = useState<string[]>([])
  const [category, setCategory] = useState('')
  const [admin, setAdmin] = useState('')
  const [subject, setSubject] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [rows, setRows] = useState<PIIAccessRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchPIICategories()
      .then(setCategories)
      .catch(() => setCategories([]))
  }, [])

  const run = useCallback(() => {
    setLoading(true)
    searchPIIAccess({ admin, category, subject, since: dayStart(since), until: dayEnd(until), limit: 300 })
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [admin, category, subject, since, until])

  const clear = () => {
    setCategory('')
    setAdmin('')
    setSubject('')
    setSince('')
    setUntil('')
    setRows(null)
  }

  const input = 'rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 text-sm text-primary outline-none'

  return (
    <>
      <div className="border-b border-border-subtle p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.category}</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={input}>
              <option value="">{S.anyCategory}</option>
              {categories.map((c) => (
                <option key={c} value={c}>{S.catLabel(c)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.staff}</span>
            <input value={admin} onChange={(e) => setAdmin(e.target.value)} placeholder="username" className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.subject}</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="table:users" className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.since}</span>
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} className={input} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-tertiary">{S.until}</span>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={input} />
          </label>
          <button onClick={run} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
            <Search size={14} />
            {S.search}
          </button>
          <button onClick={clear} className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary">
            <X size={14} />
            {S.clear}
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">
            <ShieldAlert size={24} className="mx-auto mb-2 opacity-60" />
            {S.hint}
          </div>
        ) : loading ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm text-tertiary">{S.empty}</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-2 text-xs text-tertiary">{S.results(rows.length)}</div>
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-2 py-1.5 font-medium">{S.time}</th>
                  <th className="px-2 py-1.5 font-medium">{S.staff}</th>
                  <th className="px-2 py-1.5 font-medium">{S.category}</th>
                  <th className="px-2 py-1.5 font-medium">{S.target}</th>
                  <th className="px-2 py-1.5 font-medium">{S.detail}</th>
                  <th className="px-2 py-1.5 font-medium">{S.ip}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id ?? `${r.created_at}:${r.action}:${r.target ?? ''}`)} className="border-b border-border-subtle align-top">
                    <td className="whitespace-nowrap px-2 py-1.5 text-tertiary">{fmtTime(r.created_at)}</td>
                    <td className="px-2 py-1.5 text-secondary">{r.admin ?? '—'}</td>
                    <td className="px-2 py-1.5"><CatBadge c={r.category} /></td>
                    <td className="px-2 py-1.5 font-mono text-secondary">{r.target ?? '—'}</td>
                    <td className="max-w-xs truncate px-2 py-1.5 text-secondary" title={r.detail ?? ''}>{r.detail ?? '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-tertiary">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
